import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tooltip, Skeleton } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, KeyOutlined } from "@ant-design/icons";
import { getEmployeeByAdminId, deleteEmployee } from "../../services/employeeService";
import { CreateEmployeeModal } from "../modals/CreateEmployeeModal";
import { UpdateEmployeeModal } from "../modals/UpdateEmployeeModal";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import ChangePasswordModal from "../modals/ChangePasswordModal";
import EmptyState from "../common/EmptyState";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { formatDate } from "../../utils/formatters";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const adminId = localStorage.getItem("userId");

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await getEmployeeByAdminId(adminId);
      setEmployees(response.data.content);
      setPagination({
        current: response.data.pageable.pageNumber + 1,
        pageSize: response.data.pageable.pageSize,
        total: response.data.totalElements,
      });
    } catch (error) {
      toast.error("Calisanlar yuklenemedi");
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleCreateEmployee = () => {
    setIsCreateModalVisible(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsEditModalVisible(true);
  };

  const handleDeleteEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsDeleteModalVisible(true);
  };

  const handleChangePassword = (employee) => {
    setSelectedEmployee(employee);
    setIsPasswordModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedEmployee) return;

    setDeleteLoading(true);
    try {
      await deleteEmployee(selectedEmployee.id, adminId);
      setIsDeleteModalVisible(false);
      setSelectedEmployee(null);
      fetchEmployees();
      toast.success("Calisan basariyla silindi", { icon: "🗑️" });
    } catch (error) {
      console.error("Personel silerken hata meydana geldi:", error);
      toast.error("Çalışan silinirken hata oluştu");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    setIsCreateModalVisible(false);
    fetchEmployees();
    toast.success("Calisan basariyla olusturuldu", { icon: "👤" });
  };

  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    fetchEmployees();
    toast.success("Calisan basariyla guncellendi", { icon: "✅" });
  };

  const handlePasswordSuccess = () => {
    setIsPasswordModalVisible(false);
    setSelectedEmployee(null);
    toast.success("Sifre basariyla guncellendi", { icon: "🔐" });
  };

  const columns = [
    {
      title: "Kullanici Adi",
      dataIndex: "username",
      key: "username",
      sorter: true,
      render: (text) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>@{text}</span>
      ),
    },
    {
      title: "Isim",
      dataIndex: "name",
      key: "name",
      sorter: true,
    },
    {
      title: "Soyisim",
      dataIndex: "surname",
      key: "surname",
      sorter: true,
    },
    {
      title: "Telefon Numarasi",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: "Olusturma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (
        <span style={{ color: '#8c8c8c' }}>
          {formatDate(text)}
        </span>
      ),
    },
    {
      title: "Yetkili",
      dataIndex: "adminId",
      key: "adminId"
    },
    {
      title: "Eylemler",
      key: "actions",
      render: (_, record) => (
        <Space size="small" className="table-actions">
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Duzenle" color="black">
              <Button
                icon={<EditOutlined style={{ color: '#60504f' }} />}
                onClick={() => handleEditEmployee(record)}
                size="small"
              />
            </Tooltip>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Sifre Degistir" color="blue">
              <Button
                icon={<KeyOutlined style={{ color: '#1890ff' }} />}
                onClick={() => handleChangePassword(record)}
                size="small"
              />
            </Tooltip>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Sil" color="red">
              <Button
                icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                onClick={() => handleDeleteEmployee(record)}
                size="small"
                danger
              />
            </Tooltip>
          </motion.div>
        </Space>
      ),
    },
  ];

  // Loading skeleton
  if (loading && employees.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <Skeleton.Button active style={{ width: 140 }} />
        </div>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  // Empty state
  if (!loading && employees.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateEmployee}
              className="btn-enhanced"
            >
              Calisan Ekle
            </Button>
          </motion.div>
        </div>
        <EmptyState
          type="employees"
          onAction={handleCreateEmployee}
        />
        <CreateEmployeeModal
          visible={isCreateModalVisible}
          onCancel={() => setIsCreateModalVisible(false)}
          onSuccess={handleCreateSuccess}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{ display: 'inline-block' }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateEmployee}
            className="btn-enhanced"
          >
            Calisan Ekle
          </Button>
        </motion.div>
      </div>
      <Table
        columns={columns}
        dataSource={employees}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        rowClassName="table-row-animated"
        style={{ borderRadius: 8, overflow: 'hidden' }}
      />
      <CreateEmployeeModal
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />
      <UpdateEmployeeModal
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onSuccess={handleEditSuccess}
        initialValues={selectedEmployee}
        employeeId={selectedEmployee?.id}
      />
      <ConfirmDeleteModal
        visible={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSelectedEmployee(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Çalışanı Sil"
        itemName={selectedEmployee ? `${selectedEmployee.name} ${selectedEmployee.surname}` : ""}
        loading={deleteLoading}
        warningText="Bu işlem geri alınamaz. Çalışan kalıcı olarak silinecektir."
      />
      <ChangePasswordModal
        visible={isPasswordModalVisible}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          setSelectedEmployee(null);
        }}
        onSuccess={handlePasswordSuccess}
        entity={selectedEmployee}
        entityType="employee"
      />
    </motion.div>
  );
};

export default EmployeeList;
